import React, { FC, useEffect, useRef, useState } from 'react';
import { useConnection } from '../../connection/Application';
import { makeStyles } from '@mui/styles';
import Tabs, { TabsActions } from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import ArrowTabScrollButton from '../common/ArrowTabScrollButton';
import { useQueryParam } from 'use-query-params';


const useStyles = makeStyles({
  nav: {
    position: 'sticky',
    top: 0,
    width: '100%',
    maxWidth: '1300px',
    margin: '0 auto',
    zIndex: 1,
  },
  oval: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%',
    height: '100%',
    borderRadius: '40px',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'auto',
    marginLeft: '54px',
    marginRight: '3px',
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    height: '65px',
    margin: '0 8px',
    paddingTop: '19px',
    gap: '8px',
    transition: 'height 300ms cubic-bezier(0.2, 0, 0, 1)',
    borderRadius: '0 0 40px 40px',
  },
  tab: {
    display: 'inline-flex !important',
    position: 'relative',
    flexGrow: 1,
    alignItems: 'center !important',
    justifyContent: 'center !important',
    padding: '10px 24px !important',
    minWidth: '180px !important',
    lineHeight: '23px !important',
    cursor: 'pointer !important',
    verticalAlign: 'middle !important',
  },
  active: {
    background: '#4d6936 !important',
  },
});

// #4d6936

/**
 * `onSelect` is for pages other than /home: picking a subject hands its id to
 * the page instead of setting `?category` here, and nothing is pre-selected.
 */
const Category: FC<{ sticky: boolean; onSelect?: (categoryId: string) => void }> = ({ sticky, onSelect }) => {
  const flagsContext = (require as any).context(
    '../../assets/images/icons/icons2',
    false,
    /\.png$/,
  );
  const flagOptions: { file: string; src: string }[] = flagsContext
    .keys()
    .sort()
    .map((key: string) => ({
      file: key.replace('./', '').replace('.png', ''),
      src: flagsContext(key),
    }));
  const [category, setCategory] = useQueryParam<string | undefined>('category');
  const [value, setValue] = useState<number>(-1);
  const [categories, setCategories] = useState<{ category: string; id: string, icon?: string }[]>([]);
  const connection = useConnection();
  const classes = useStyles();
  const ref = useRef<TabsActions | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const category1 = await connection.get('category/data/all');
      setCategories(
        category1.response.body.map((e: any, i: number) => ({
          category: e.value,
          id: e.id,
          icon: e?.icon,
        })),
      );
      if (category) {
        setValue(category1.response.body.findIndex((e: any) => e.id === category));
      } else if (!onSelect) {
        setCategory(category1.response.body?.[0]?.id);
        setValue(0);
      }
    };
    fetchData();
  }, [connection]);

  useEffect(() => {
    setTimeout(() => {
      ref.current?.updateIndicator();
      ref.current?.updateScrollButtons();
    }, 700);
  }, [sticky]);

  useEffect(() => {
    if (category) {
      setValue(categories.findIndex((e) => e.id === category));
    } else {
      setValue(-1);
    }
  }, [category]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    if (onSelect) {
      onSelect(categories[newValue].id);
      return;
    }
    setCategory(categories[newValue].id);
  };
  return (
    <nav role='presentation' className={classes.nav} aria-label='article tabs'>
      <div className={classes.container}>
        <Tabs
          action={ref}
          sx={{
            overflow: 'auto',
            '& .MuiTabs-scrollButtons': {
              color: '#0781C4 !important',
            },
            '& .MuiTabs-scrollButtons.Mui-disabled': {
              opacity: 0.3,
            },
            '& .MuiTabs-flexContainer': {
              height: '100%',
            },
            '& .MuiTabs-scroller': {
              margin: '0 60px !important',
              // background: '#6750A429',
              // borderRadius: '15px 15px 0 0',
          },
          }}
          TabIndicatorProps={{ style: { background: 'none' } }}
          className={classes.oval}
          variant='scrollable'
          scrollButtons={categories.length > 4 ? (categories.length > 6 ? true : 'auto') : false}
          value={value}
          onChange={handleChange}
          aria-label='icon label tabs example'
          ScrollButtonComponent={ArrowTabScrollButton}
        >
          {categories.map((e, i) => (
            <Tab
              key={e.id}
              className={classes.tab}
              sx={{
                borderRadius:  value === i ? '15px !important' : '0',
                '&:not(:first-of-type)': {
                  borderLeft: value === i || value === i - 1 ? 'none': '1.5px solid #B3B3B3',
                },
                backgroundColor: value === i ? '#0781C4 !important' : 'none',
                color: value === i ? 'white !important' : '#545454',
              }}
              label={(
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}>
                  {e.category}
                </div>
              )}
            />
          ))}
        </Tabs>
      </div>
    </nav>
  );
};

export default Category;
